/**
 * The pieces the signing page and the Request access modal's signing panel
 * share: the round signing badge, and what is being signed as a sentence and
 * labelled rows (from `describePayload`). `compact` is the panel's tighter
 * spacing; the words are the same.
 */
import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import type { ReactNode } from "react";
import type { IconType } from "react-icons";

import type { StepUpChallenge } from "state/members/stepUp";

import { describePayload, type KnownAccount } from "./payloadProse";

export type Tone = "p" | "success" | "error" | "warning";

/** Why an expired challenge signed nothing; the page and the panel both say it. */
export const EXPIRED_LINE = "Signing requests last five minutes. Nothing was signed.";

/** What a used challenge means, and who to tell if it was not them. */
export const USED_LINE =
  "Each one can be signed once. If you did not sign it, tell your IT or security team.";

// The ring breathes while the browser's prompt is open. Purpose: a wait the
// page does not control; the badge shows it is not stuck. Reduced motion:
// no ring.
const breathe = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(1.22); opacity: 0; }
`;

/** The large round badge that heads the signing page and the panel. */
export const SigningBadge = ({
  tone,
  icon,
  waiting = false,
  testId,
}: {
  tone: Tone;
  icon: IconType;
  waiting?: boolean;
  testId?: string;
}) => (
  <Flex
    data-testid={testId}
    data-tone={tone}
    position="relative"
    align="center"
    justify="center"
    boxSize="104px"
    borderRadius="full"
    bg={`${tone}.50`}
    flexShrink={0}
  >
    {waiting && (
      <Box
        position="absolute"
        inset={0}
        borderRadius="full"
        borderWidth="2px"
        borderColor={`${tone}.200`}
        animation={`${breathe} 1.6s cubic-bezier(0.23, 1, 0.32, 1) infinite`}
        sx={{ "@media (prefers-reduced-motion: reduce)": { display: "none" } }}
      />
    )}
    <Flex
      align="center"
      justify="center"
      boxSize="72px"
      borderRadius="full"
      bg="neutral.0"
      color={`${tone}.600`}
      boxShadow="elevLow"
    >
      <Box as={icon} boxSize="36px" aria-hidden />
    </Flex>
  </Flex>
);

const Row = ({
  label,
  compact,
  children,
}: {
  label: string;
  compact: boolean;
  children: ReactNode;
}) => (
  <Flex
    direction={compact ? "row" : { base: "column", sm: "row" }}
    align={compact ? "baseline" : { base: "flex-start", sm: "baseline" }}
    gap={compact ? "md" : { base: "2xs", sm: "lg" }}
    px={compact ? "sm" : "md"}
    py={compact ? "1xs" : "md"}
    borderBottomWidth="1px"
    borderBottomColor="neutral.200"
    _last={{ borderBottomWidth: 0 }}
  >
    <Box
      as="dt"
      fontSize="xs"
      fontWeight={500}
      color="neutral.700"
      minW={compact ? "80px" : "112px"}
      flexShrink={0}
    >
      {label}
    </Box>
    <Box as="dd" fontSize="smd" color="neutral.950" wordBreak="break-word">
      {children}
    </Box>
  </Flex>
);

/** What a challenge's payload asks for, in words. Never raw JSON. */
export const PayloadSummary = ({
  challenge,
  account,
  compact = false,
}: {
  challenge: StepUpChallenge;
  account?: KnownAccount | null;
  compact?: boolean;
}) => {
  const summary = describePayload(challenge.payload, challenge.purpose, account);
  return (
    <Stack spacing={compact ? "sm" : "lg"} w="full">
      <Text fontSize="md" color="neutral.900">
        {summary.sentence}
      </Text>
      <Box
        as="dl"
        w="full"
        bg="neutral.50"
        borderWidth="1px"
        borderColor="neutral.200"
        borderRadius="component.xl"
      >
        {summary.rows.map((row) => (
          <Row key={row.label} label={row.label} compact={compact}>
            {row.value}
          </Row>
        ))}
      </Box>
    </Stack>
  );
};
